import { useState } from "react";
import Cookies from "js-cookie";
import { postApi, putApi } from "../../../../utils/api.ts";
import { CREATE_WFH_TIMESHEET, API_RESUBMIT_WFH_TIMESHEET } from "../../../../../config/Endpoints.js";
import { TimesheetEntry } from "../types.ts";
import { showSuccess, showError, showWarning } from "../../../../utils/toast.ts";

export interface SaveTimesheetPayload {
  date: string;
  isSubmitted: string;
  entries: {
    entryId?: string;
    titleType: string;
    projectId: number | null;
    titleName: string | null;
    categoryId?: number | null;
    categoryName?: string | null;
    phase?: TimesheetEntry["phase"];
    description: string;
    duration: number;
    taskStatus: string;
    workStream?: string | null;
    workStreamId?: number | null;
    isModified?: boolean;

  }[];
}

export function useTimesheetMutation() {
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const accessToken = Cookies.get("accessToken");

  const validateTimesheet = (entries: TimesheetEntry[]) => {
    if (entries.length === 0) {
      showWarning("Please add at least one entry.");
      return false;
    }

    let totalMinutes = 0;
    for (const entry of entries) {
      const duration = entry.hours * 60 + (Number(entry.minutes) || 0);

      // All fields mandatory check
      if (!entry.projectId && !entry.category) {
        showError("All fields are mandatory (Project/Category missing).");
        return false;
      }
      if (!entry.description?.trim()) {
        showError("All fields are mandatory (Description missing).");
        return false;
      }
      if (duration === 0) {
        showError("Time duration cannot be zero.");
        return false;
      }
      if (!entry.status) {
        showError("All fields are mandatory (Status missing).");
        return false;
      }

      totalMinutes += duration;
    }

    if (totalMinutes > 40 * 60) {
      showError("Daily Total time cannot exceed 40 hours.");
      return false;
    }

    return true;
  };

  const mutateTimesheet = async (
    date: Date,
    entries: TimesheetEntry[],
    isSubmittedValue: number,
    draftId: number | null,
    isResubmit: boolean = false
  ) => {
    if (entries.length === 0) {
      toast.warning("Please add at least one entry.");
      return undefined;
    }

    // Full validation only when submitting for approval — draft save runs only from "Save Draft".
    if (isSubmittedValue === 1) {
      if (!validateTimesheet(entries)) return undefined;
    }

    if (isSubmittedValue === 1) setIsSubmitting(true);
    else setIsSavingDraft(true);

    const formattedDate = date.toLocaleDateString("en-CA");

    const payload: SaveTimesheetPayload = {
      date: formattedDate,
      isSubmitted: String(isSubmittedValue),
      entries: entries.map((entry) => {
        const baseEntry: any = {
          entryId: entry.id,
          titleType: entry.titleType || (entry.projectId ? "PROJECT" : "OTHERS"),
          projectId: entry.projectId || null,
          titleName: entry.category,
          categoryId: entry.categoryId ?? null,
          categoryName: entry.category,
          phase: entry.phase ?? null,
          phaseId: entry.phaseId ?? null,
          phaseCode: entry.phaseCode ?? null,
          phaseName: entry.phaseName ?? null,
          description: entry.description,
          duration: entry.hours * 60 + (Number(entry.minutes) || 0),
          taskStatus: entry.status.toUpperCase().replace(/-/g, ""),
          ...(entry.workStream ? { workStream: entry.workStream } : {}),
          workStreamId: entry.workStreamId ?? null,
        };

        if (isResubmit) {
          baseEntry.isModified = entry.isModified ?? false;
        }

        const isProject = baseEntry.titleType === "PROJECT";
        if (isProject) {
          return { ...baseEntry, allocationId: (entry as any).allocationId || null };
        }

        return baseEntry;
      }),
    };

    let isPut = isResubmit && isSubmittedValue === 1;
    let url = isPut ? API_RESUBMIT_WFH_TIMESHEET : CREATE_WFH_TIMESHEET;
    if (draftId && url !== API_RESUBMIT_WFH_TIMESHEET) {
      url = `${url}?id=${draftId}`;
    }

    try {
      const { data }: any = isPut
        ? await putApi(url, accessToken, payload as any)
        : await postApi(url, accessToken, payload as any);

      if (Number(data?.statusCode) >= 400) {
        throw new Error(
          typeof data?.message === "string" && data.message.trim()
            ? data.message
            : "Unable to save the timesheet.",
        );
      }

      const successMsg = typeof data === 'string'
        ? data
        : (data?.message || data?.response || (isSubmittedValue === 1 ? "Timesheet submitted successfully." : "Draft saved successfully."));
      showSuccess(successMsg);
      return data;
    } catch (err: any) {
      showError(err.message || "Something went wrong");
      return undefined;
    } finally {
      setIsSubmitting(false);
      setIsSavingDraft(false);
    }
  };

  return { mutateTimesheet, isSavingDraft, isSubmitting };
}
